import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, fmtDate } from '../../components/ui';
import { Kpis, Panel, BarsH, Donut } from '../../components/charts';
import { fmtInt, mondayOf, todayIso, withParams } from './Week';

/* ============================================================
   Dashboard team.

   Cakupan PIC nol bukan keadaan kosong yang perlu ditutup dengan
   spinner atau layar "belum ada data". Ia temuan: berkas MS Project
   menyebut kru, bukan orang, jadi sampai kru dipetakan tidak ada
   siapa pun yang bisa ditanyai hari Senin pagi. Halaman ini
   mengatakannya, lalu menunjukkan jalan keluarnya.
   ============================================================ */

type Summary = {
  week: { from: string; to: string };
  people: { total: number; activeThisWeek: number };
  resources: { total: number; mapped: number; unmapped: number };
  load: { overloaded: number; threshold: number; top: { name: string; load: number; late: number }[] };
  coverage: { activities: number; assigned: number; unassigned: number; pct: number | null };
};

export function Dashboard({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  /* Jangkar minggu dikunci sekali saat halaman dibuka dan dikirim
     eksplisit, supaya angka di sini sama persis dengan papan
     mingguan yang dibuka dari sini — juga kalau tengah malam lewat
     di antara keduanya. */
  const [week] = useState(() => mondayOf(params.get('week') ?? todayIso()));

  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Summary>(`/team/summary?projectId=${projectId}&week=${week}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the team summary.');
    }
  }, [projectId, week]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const { coverage, resources, people, load: workload } = data;
  const weekHref = withParams('/week', params, { week });
  const peopleHref = withParams('/people', params);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Resource Control</h1>
          <p>
            Minggu ini siapa mengerjakan apa — {fmtDate(data.week.from)} sampai {fmtDate(data.week.to)}.
          </p>
        </div>
      </div>

      {people.total === 0 && resources.total > 0 && (
        <div className="banner">
          Berkas programme proyek ini menyebut kru, bukan orang: {fmtInt(resources.total)} resource
          dan belum satu pun orang terdaftar. Selama itu, {fmtInt(coverage.unassigned)} aktivitas
          minggu ini tidak punya nama yang bisa ditanyai hari Senin pagi.{' '}
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 8 }}
            onClick={() => nav(peopleHref)}
          >
            Petakan kru ke orang
          </button>
        </div>
      )}

      {resources.total === 0 && (
        <div className="banner">
          Belum ada resource sama sekali di proyek ini. Resource datang dari versi programme yang
          diterima di prog., jadi papan mingguan akan tetap kosong sampai ada satu yang diterima.
        </div>
      )}

      <Kpis
        items={[
          {
            label: 'Aktivitas minggu ini',
            value: fmtInt(coverage.activities),
            note: `${fmtInt(coverage.assigned)} sudah ada namanya`,
          },
          {
            label: 'Cakupan PIC',
            value: coverage.pct == null ? '—' : `${coverage.pct}%`,
            note: coverage.pct == null ? 'belum ada aktivitas untuk diukur' : undefined,
            tone: coverage.pct == null ? undefined : coverage.pct >= 80 ? 'good' : 'crit',
          },
          {
            label: 'Orang terdaftar',
            value: fmtInt(people.total),
            note: `${fmtInt(people.activeThisWeek)} memegang pekerjaan minggu ini`,
            tone: people.total === 0 ? 'crit' : undefined,
          },
          {
            label: 'Kelebihan beban',
            value: fmtInt(workload.overloaded),
            note: `ambang ${workload.threshold} aktivitas sepekan`,
            tone: workload.overloaded > 0 ? 'warn' : undefined,
          },
        ]}
      />

      <div className="grid-2">
        <Panel
          title="Cakupan PIC"
          note="Bagian aktivitas minggu ini yang punya orang di belakangnya, bukan hanya kru."
        >
          {coverage.activities === 0 ? (
            <p className="hint">
              Tidak ada aktivitas yang berjalan minggu ini, jadi cakupannya belum terukur — bukan nol.
            </p>
          ) : (
            <Donut
              slices={[
                { name: 'Ada PIC', value: coverage.assigned, color: 'var(--series-1)' },
                { name: 'Tanpa PIC', value: coverage.unassigned, color: 'var(--status-critical)' },
              ]}
              centre={coverage.pct == null ? '—' : `${coverage.pct}%`}
              centreNote={`dari ${fmtInt(coverage.activities)} aktivitas`}
            />
          )}
        </Panel>

        <Panel
          title="Beban per orang"
          note={`Di atas ${workload.threshold} aktivitas sepekan mutu mulai turun. Angkanya kebijakan, bukan hukum.`}
        >
          {workload.top.length === 0 ? (
            <p className="hint">
              Belum ada nama yang bisa dibebani pekerjaan. Petakan kru ke orang lebih dulu.
            </p>
          ) : (
            <BarsH
              rows={workload.top.map((p) => ({
                label: p.name,
                value: p.load,
                tone: p.load >= workload.threshold ? 'crit' : 'accent',
              }))}
            />
          )}
        </Panel>
      </div>

      <Panel
        title="Yang perlu dijawab"
        note="Setiap angka di sini membuka barisnya sendiri — yang mana, bukan berapa."
        wide
      >
        <div className="tbl-wrap">
          <table className="tbl">
            <tbody>
              <tr onClick={() => nav(weekHref)}>
                <td>Aktivitas minggu ini tanpa PIC</td>
                <td className={coverage.unassigned > 0 ? 'num neg' : 'num'}>
                  {fmtInt(coverage.unassigned)}
                </td>
                <td className="hint">buka papan mingguan</td>
              </tr>
              <tr onClick={() => nav(peopleHref)}>
                <td>Resource yang belum dipetakan ke orang</td>
                <td className={resources.unmapped > 0 ? 'num neg' : 'num'}>
                  {fmtInt(resources.unmapped)} dari {fmtInt(resources.total)}
                </td>
                <td className="hint">buka daftar orang</td>
              </tr>
              <tr onClick={() => nav(peopleHref)}>
                <td>Orang terdaftar</td>
                <td className={people.total === 0 ? 'num neg' : 'num'}>{fmtInt(people.total)}</td>
                <td className="hint">tambah orang</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
